import React, { useEffect, useRef, useState } from "react";
import { Alert, AlertDescription, AlertTitle, Button } from "@jem-open/jem-ui";
import { ArrowRight, ExternalLink, X } from "lucide-react";
import { useMigrationStatus } from "./useMigrationStatus";
import { WEBINAR_SESSIONS } from "./constants";

// Null while the profile loads — a shared anonymous key would let one user's
// dismissal hide the banner from the next.
const scheduledDismissKey = (userId: number | undefined): string | null =>
  userId === undefined ? null : `jem2_scheduled_banner_dismissed:${userId}`;

const readScheduledDismissed = (userId: number | undefined): boolean => {
  const key = scheduledDismissKey(userId);
  if (key === null) return false;
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const persistScheduledDismissed = (userId: number | undefined): void => {
  const key = scheduledDismissKey(userId);
  if (key === null) return;
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    /* localStorage unavailable — dismissal just won't persist */
  }
};

// Inset card rather than a flush edge-to-edge strip. The inset lives on a
// wrapper (BANNER_WRAPPER_CLASSNAME) rather than as a margin here: Alert is
// w-full, so width:100% plus a margin would overflow the viewport on x.
const BANNER_CLASSNAME =
  "rounded-md border px-lg py-lg flex flex-col gap-xs sm:flex-row sm:items-center sm:justify-between";

const BANNER_WRAPPER_CLASSNAME = "p-lg";

export const MigrationBanner: React.FC = () => {
  const { isMigrated, isScheduled, newHubUrl, userId } = useMigrationStatus();
  const [scheduledDismissed, setScheduledDismissed] = useState(() =>
    readScheduledDismissed(userId)
  );

  // Re-read on identity change: undefined -> id after the profile loads, and
  // id -> id on an account switch.
  const lastUserId = useRef(userId);
  useEffect(() => {
    if (lastUserId.current === userId) return;
    lastUserId.current = userId;
    setScheduledDismissed(readScheduledDismissed(userId));
  }, [userId]);

  if (isMigrated) {
    return (
      <div className={BANNER_WRAPPER_CLASSNAME}>
        <Alert variant="note" hideIcon className={BANNER_CLASSNAME}>
          <div className="flex-1">
            <AlertTitle className="text-xl font-semibold text-greyscale-text-title">
              Jem Hub 2.0 is live 🎉
            </AlertTitle>
            <AlertDescription className="pl-0 text-sm">
              {/* Block spans rather than nested <p>: AlertDescription already
                  renders a paragraph, and <p> inside <p> is invalid markup. */}
              <span className="block">
                Your workspace just got upgraded. Same login, new home, plenty
                that&apos;s new.
              </span>
              <span className="mt-sm block">
                Login with your same credentials on this link{" "}
                <a
                  href={newHubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-jem-link underline underline-offset-2 transition-colors hover:text-jem-link-hover focus-visible:outline-offset-2"
                >
                  {newHubUrl}
                </a>{" "}
                and if you&apos;d like a proper tour of what&apos;s changed,
                register for one of our{" "}
                <strong className="font-semibold">webinar sessions</strong> next
                week:
              </span>
            </AlertDescription>
            <ul className="mt-sm flex flex-col gap-xs">
              {WEBINAR_SESSIONS.map((session) => (
                <li key={session.url}>
                  <a
                    href={session.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-xs text-sm font-semibold text-jem-link underline underline-offset-2 transition-colors hover:text-jem-link-hover focus-visible:outline-offset-2"
                  >
                    {session.label}
                    <ExternalLink size={16} aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-sm text-xs font-medium text-jem-link">
              (*Note: Once you move over to the new Jem Hub 2.0, you cannot
              continue using this old hub as they are not backward compatible)
            </p>
          </div>
        </Alert>
      </div>
    );
  }

  if (isScheduled && !scheduledDismissed) {
    // Without an id the dismissal cannot be stored, so hiding would only last
    // until the profile resolves and the banner reappeared.
    const dismiss = (): void => {
      if (userId === undefined) return;
      persistScheduledDismissed(userId);
      setScheduledDismissed(true);
    };
    return (
      <div className={BANNER_WRAPPER_CLASSNAME}>
        <Alert variant="note" hideIcon className={BANNER_CLASSNAME}>
          <div className="flex-1">
            <AlertTitle className="text-sm font-semibold text-greyscale-text-title">
              The new Jem Hub is live from 4 August.
            </AlertTitle>
            <AlertDescription className="pl-0 text-xs">
              Your workspace is moving to hub.jemhr.online — one place to
              connect, manage and reward your workforce. Same login, new home.
              Take a look whenever you are ready.
            </AlertDescription>
          </div>
          <div className="flex items-center gap-sm shrink-0">
            <Button asChild>
              <a href={newHubUrl} target="_blank" rel="noopener noreferrer">
                Go to the new Jem Hub
                <ArrowRight size={16} className="ml-xs" />
              </a>
            </Button>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={dismiss}
              className="p-xs text-greyscale-text-body hover:text-greyscale-text-title"
            >
              <X size={16} />
            </button>
          </div>
        </Alert>
      </div>
    );
  }

  return null;
};

export default MigrationBanner;
